/**
 * Create Impact — vertical composition: shared hooks + sections + ImpactFields.
 */
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthGate } from "hooks/useAuthGate";
import { useMediaManager } from "hooks/useMediaManager";
import { usePostFormLifecycle } from "hooks/usePostFormLifecycle";
import { useResolvedIssue, type IssueEntityInput } from "hooks/useResolvedIssue";
import { useCoauthorManager } from "hooks/useCoauthorManager";
import { usePrivacyDefaults } from "hooks/usePrivacyDefaults";
import { useUrlTitlePrefill } from "hooks/useUrlTitlePrefill";
import TextEditor from "components/TextEditor";
import { Loader2 } from "lucide-react";
import { Impact, Scope } from "util/constants";
import {
  addCoauthors,
  createImpactForIssue,
  uploadEntityMedia,
} from "util/posts";
import { standardizeUrl } from "util/textUtils";
import { impactCreateSchema } from "lib/validation/contentSchemas";
import PostFormShell from "components/Posts/PostFormShell";
import MediaSection from "components/Posts/sections/MediaSection";
import LinkField from "components/Posts/sections/LinkField";
import TitleField from "components/Posts/sections/TitleField";
import CoauthorSection from "components/Posts/sections/CoauthorSection";
import PrivacySection from "components/Posts/sections/PrivacySection";
import FormStatusMessage from "components/Posts/sections/FormStatusMessage";
import ImpactFields from "components/Posts/kindFields/ImpactFields";
import { PrivacyLevel } from "util/userUtils";

export interface PostFormHandle {
  submit: () => void;
}

interface Props {
  entity: IssueEntityInput;
  onSuccess: (impact: Impact) => void;
  onCancel: () => void;
  hideActions?: boolean;
  onCanSubmitChange?: (canSubmit: boolean) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

const ImpactPostForm = forwardRef<PostFormHandle, Props>(
  (
    {
      entity,
      onSuccess,
      onCancel,
      hideActions = true,
      onCanSubmitChange,
      onSubmittingChange,
    },
    ref
  ) => {
    const nav = useNavigate();
    const { requireAuth, currentUser } = useAuthGate();
    const { resolvedIssue: impactIssue } = useResolvedIssue(entity);
    const { privacy, setPrivacy } = usePrivacyDefaults();
    const { coauthors, addCoauthor, removeCoauthor, coauthorPairs } = useCoauthorManager(privacy);

    const mm = useMediaManager(20);
    const [referenceLink, setReferenceLink] = useState("");
    const [scope, setScope] = useState<Scope | "">("");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [impactType, setImpactType] = useState<"impact_positive" | "impact_negative" | null>(null);
    const [showLink, setShowLink] = useState(false);
    const [showTitle, setShowTitle] = useState(false);

    const { meta, loading } = useUrlTitlePrefill({
      linkEnabled: showLink,
      referenceLink,
      titleToggleRequiredForPrefill: true,
      titleEnabled: showTitle,
      setTitle,
    });

    const lifecycle = usePostFormLifecycle({
      getValues: useCallback(
        () => ({
          showLink,
          showTitle,
          referenceLink,
          title,
          description,
          mediaCount: mm.media.length,
        }),
        [showLink, showTitle, referenceLink, title, description, mm.media.length]
      ),
      schema: impactCreateSchema,
      isReady: Boolean(impactIssue) && !mm.isUploading,
      onCanSubmitChange,
      onSubmittingChange,
    });

    const maybeUploadReferenceImage = useCallback(
      async (impactId: number) => {
        const url = meta?.image?.[0]?.url;
        if (!url) return;
        try {
          const r = await fetch(url);
          const blob = await r.blob();
          const ext = (blob.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
          const file = new File([blob], `reference_image.${ext}`, { type: blob.type });
          await uploadEntityMedia("impact", impactId, [file], [0]);
        } catch {
          /* best-effort */
        }
      },
      [meta?.image]
    );

    /**
     * Validates auth and payload, creates the impact, uploads media, then navigates on success.
     */
    const submit = useCallback(async () => {
      if (!requireAuth()) return;
      if (!impactIssue) {
        lifecycle.setError("Issue is required.");
        return;
      }
      const validationError = lifecycle.validateOrError();
      if (validationError) {
        lifecycle.setError(validationError);
        return;
      }
      if (!currentUser) return;

      lifecycle.beginSubmit();

      try {
        const link = showLink ? standardizeUrl(referenceLink) : null;
        const finalTitle = showTitle ? title.trim() || "[no_title]" : "[no_title]";
        const properties: Record<string, string | PrivacyLevel | undefined> = { privacy };

        if (showLink && meta) {
          properties.reference_link_title = meta.title || finalTitle;
          properties.reference_link_description = meta.description;
          properties.reference_link_siteName = meta.siteName;
          properties.reference_link_image_url = meta.image?.[0]?.url;
          properties.reference_link_image_type = meta.image?.[0]?.type;
        }

        const payload = {
          url: link,
          body: description.trim(),
          title: showTitle ? finalTitle : "[no_title]",
          rel_type: impactType || "impact_inactive",
          user_id: currentUser.id,
          properties: { ...properties, scope: scope || null },
        };

        const created = await createImpactForIssue(impactIssue.id, payload);
        const createdId = created.impact_id ?? created.id;

        if (coauthors.length > 0 && privacy !== "anonymous") {
          await addCoauthors("impact", createdId, coauthorPairs).catch(() => {});
        }

        if (mm.media.length > 0) {
          await uploadEntityMedia(
            "impact",
            createdId,
            mm.media.map((m) => m.file),
            mm.orders()
          ).catch(() => {});
        }

        if (showLink) {
          await maybeUploadReferenceImage(createdId);
        }

        onSuccess(created);
        nav(`/impact/${createdId}`);
      } catch (e: unknown) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status !== 401) {
          lifecycle.setError("Failed to add impact. Please try again.");
        }
      } finally {
        lifecycle.endSubmit();
      }
    }, [
      requireAuth,
      impactIssue,
      showLink,
      showTitle,
      referenceLink,
      title,
      description,
      mm,
      currentUser,
      impactType,
      privacy,
      scope,
      meta,
      coauthors.length,
      coauthorPairs,
      maybeUploadReferenceImage,
      onSuccess,
      nav,
      lifecycle,
    ]);

    useImperativeHandle(ref, () => ({ submit }), [submit]);

    const linkField = useMemo(
      () => (
        <LinkField
          id="impact-reference-link"
          name="impact-reference-link"
          labelText="Reference URL"
          value={referenceLink}
          onChange={setReferenceLink}
        />
      ),
      [referenceLink]
    );

    const titleField = useMemo(
      () => (
        <TitleField
          id="impact-title"
          name="impact-title"
          labelText="Title"
          value={title}
          onChange={setTitle}
          loading={loading}
          maxLength={300}
        />
      ),
      [title, loading]
    );

    return (
      <PostFormShell
        intro="Document an impact of this issue; impacts are the effects of an issue's influence on society. They should have at least images/videos or a title; optionally, denote the type and scope of the impact."
      >
        <MediaSection
          idPrefix="impact"
          mm={mm}
          showLink={showLink}
          showTitle={showTitle}
          onToggleLink={() =>
            setShowLink((prev) => {
              const next = !prev;
              if (next) setShowTitle(true);
              return next;
            })
          }
          onToggleTitle={() =>
            setShowTitle((prev) => {
              const next = !prev;
              if (!next) setTitle("");
              return next;
            })
          }
          linkField={linkField}
          titleField={titleField}
        />

        <ImpactFields
          impactType={impactType}
          onImpactTypeChange={setImpactType}
          scope={scope}
          onScopeChange={setScope}
        />

        <div className="form-control">
          <TextEditor value={description} onChange={setDescription} editorType="entity" placeholder="Body" />
        </div>

        {privacy !== "anonymous" ? (
          <CoauthorSection coauthors={coauthors} onAdd={addCoauthor} onRemove={removeCoauthor} />
        ) : null}

        <PrivacySection
          privacy={privacy}
          onPrivacyChange={setPrivacy}
          miniIcon={false}
          showDropdown
        />

        <FormStatusMessage error={lifecycle.error} />

        {!hideActions ? (
          <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mt-auto space-y-2 sm:space-y-0 sm:gap-x-2 sm:flex sm:justify-end sm:flex-row">
            <button type="button" onClick={onCancel} className="btn-secondary w-full sm:w-fit py-2 px-4">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!lifecycle.canSubmit}
              className={`w-full sm:w-fit py-2 px-4 ${
                !lifecycle.canSubmit
                  ? "btn-primary-disabled opacity-50 cursor-not-allowed"
                  : "btn-primary"
              }`}
            >
              {lifecycle.isSubmitting || mm.isUploading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  {lifecycle.isSubmitting ? "Submitting…" : "Uploading…"}
                </span>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        ) : null}
      </PostFormShell>
    );
  }
);

ImpactPostForm.displayName = "ImpactPostForm";

export default ImpactPostForm;
